import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxButton } from '../button/button';
import { HxDialogService } from '../dialog/dialog.service';
import { HxIcon } from '../icon/icon';
import { HxInput } from '../input/input';
import { RowsPerPageOption } from '../pagination';
import { HxPagination } from '../pagination/pagination';
import { HxPopoverDirective } from '../popover/popover';
import {
  HxTableCellDirective,
  HxTableFooterDirective,
  HxTableHeaderDirective,
  HxTableRowDirective,
} from '../table/directives';
import { HxExpandableRowComponent } from '../table/expandable-row';
import { HxHeaderCell } from '../table/header-cell';
import { HxTableComponent } from '../table/table';
import { type ColumnCustomizationResult, HxColumnCustomizationDialogComponent } from './column-customization-dialog';
import { HxTableEmptyStateComponent } from './empty-state/table-empty-state';
import type {
  CellRendererParams,
  ColumnDefinition,
  ExpansionChangeEvent,
  FilterChangeEvent,
  MainTableSortEvent,
  ManagedTableConfig,
  ManagedTableRow,
  SubTableSortEvent,
} from './types';

/**
 * Harmony Managed Table Component (hx-managed-table)
 *
 * Config-driven expandable table for hierarchical data with automatic state management. Developer provides
 * configuration and data; component handles all expansion logic internally.
 *
 * Features: Config-driven schema, automatic expansion state, sorting, keyboard navigation (WCAG 2.1 AA), ARIA
 * disclosure pattern, responsive design, signal-based state with OnPush.
 *
 * @example
 *   ```html
 *   <hx-managed-table [config]="tableConfig" [data]="tableData"
 *     (expansionChange)="onExpansionChange($event)">
 *   </hx-managed-table>
 *   ```;
 */
@Component({
  selector: 'hx-managed-table',
  standalone: true,
  providers: [HxDialogService],
  imports: [
    NgComponentOutlet,
    HxButton,
    HxExpandableRowComponent,
    HxHeaderCell,
    HxIcon,
    HxInput,
    HxPagination,
    HxPopoverDirective,
    HxTableCellDirective,
    HxTableComponent,
    HxTableEmptyStateComponent,
    HxTableFooterDirective,
    HxTableHeaderDirective,
    HxTableRowDirective,
  ],
  templateUrl: './managed-table.html',
  styleUrl: './managed-table.css',
  exportAs: 'hxManagedTable',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxManagedTableComponent extends HxBaseControl {
  // ============================================================================
  // DEPENDENCIES
  // ============================================================================

  /** Dialog service for opening column customization dialog */
  private readonly dialogService = inject(HxDialogService);

  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Table configuration defining main and sub-table column schemas */
  readonly config = input.required<ManagedTableConfig>();

  /** Data array with hierarchical structure (main rows with optional sub-data) */
  readonly data = input.required<ManagedTableRow[]>();

  /** Whether all rows should be initially expanded on first render */
  readonly initiallyExpanded = input<boolean>(false);

  /** Override aria-label for accessibility */
  override readonly ariaLabel = input<string | undefined>('Expandable data table');

  /** Maximum height for table scrolling */
  readonly maxHeight = input<string | undefined>('600px');

  /** Enable row hover effect */
  readonly enableRowHover = input<boolean>(true);

  /** Message to display when no data is available */
  readonly noDataMessage = input<string>('No data available');

  /** Enable pagination */
  readonly enablePagination = input<boolean>(false);

  /**
   * Visual variant for the table header.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around header, no vertical divider lines between cells
   * - 'dividers': No border around header, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly headerVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('dividers');

  /**
   * Visual variant for the table rows.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around rows, no vertical divider lines between cells
   * - 'dividers': No border around rows, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly rowVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('dividers');

  /** Current page number (1-based) - two-way binding for pagination control */
  readonly currentPage = model<number>(1);

  /** Items per page - two-way binding for pagination control */
  readonly pageSize = model<number>(10);

  /** Page size options for pagination dropdown */
  readonly pageSizeOptions = input<RowsPerPageOption[]>([
    { id: 'page-size-10', value: 10, label: '10' },
    { id: 'page-size-25', value: 25, label: '25' },
    { id: 'page-size-50', value: 50, label: '50' },
    { id: 'page-size-100', value: 100, label: '100' },
  ]);

  /** Placeholder text for global search input */
  readonly globalSearchPlaceholder = input<string>('Search');

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  /** Emitted when a row's expansion state changes */
  readonly expansionChange = output<ExpansionChangeEvent>();

  /** Emitted when main table is sorted */
  readonly mainTableSort = output<MainTableSortEvent>();

  /** Emitted when a sub-table is sorted */
  readonly subTableSort = output<SubTableSortEvent>();

  /** Emitted when a filter value changes (optional - for tracking purposes) */
  readonly filterChange = output<FilterChangeEvent>();

  // ============================================================================
  // STATE SIGNALS
  // ============================================================================

  /** Expansion state map: rowId → isExpanded */
  readonly expansionState = signal<Map<string | number, boolean>>(new Map());

  /** Current sort state for main table: { field, direction } | null */
  readonly mainSortState = signal<{ field: string; direction: 'asc' | 'desc' } | null>(null);

  /** Sort states for sub-tables: parentRowId → { field, direction } */
  readonly subTableSortStates = signal<Map<string | number, { field: string; direction: 'asc' | 'desc' }>>(new Map());

  /** Filter values map: field → filter text */
  readonly filterValues = signal<Map<string, string>>(new Map());

  /** Global search value */
  readonly globalSearchValue = signal<string>('');

  /** Visible column fields set (applied state) */
  readonly visibleColumnFields = signal<Set<string>>(new Set());

  // ============================================================================
  // COMPUTED SIGNALS
  // ============================================================================

  /** Computed: number of currently expanded rows */
  readonly expandedCount = computed(() => {
    const state = this.expansionState();
    return Array.from(state.values()).filter((expanded) => expanded).length;
  });

  /** Computed: whether any rows are expanded */
  readonly hasExpandedRows = computed(() => this.expandedCount() > 0);

  /** Computed: whether table has any data */
  readonly hasData = computed(() => this.data().length > 0);

  /** Computed: whether table has any expandable rows (rows with subData) */
  readonly hasExpandableRows = computed(() => this.data().some((row) => row.subData && row.subData.length > 0));

  /** Computed: displayed columns (visible only) */
  readonly displayedColumns = computed(() => {
    const visible = this.visibleColumnFields();
    const schema = this.config().mainSchema;

    // Filter to only visible columns
    return schema.filter((col) => visible.has(col.field));
  });

  /** Computed: filtered data based on active filters */
  readonly filteredData = computed(() => {
    let rows = this.data();
    const globalSearch = this.globalSearchValue().toLowerCase();
    const columnFilters = this.filterValues();
    const visibleFields = Array.from(this.visibleColumnFields());

    // Apply global search across all visible columns
    if (globalSearch) {
      rows = rows.filter((row) => {
        return visibleFields.some((field) => {
          const value = row.data[field];
          return value?.toString().toLowerCase().includes(globalSearch);
        });
      });
    }

    // Apply column-specific filters
    if (columnFilters.size > 0) {
      rows = rows.filter((row) => {
        // Check if row matches all active filters
        for (const [field, filterValue] of columnFilters.entries()) {
          if (!filterValue) {
            continue; // Skip empty filter values
          }

          const cellValue = row.data[field];
          const cellString = cellValue?.toString().toLowerCase() || '';
          const filterString = filterValue.toLowerCase();

          if (!cellString.includes(filterString)) {
            return false; // Row doesn't match this filter
          }
        }
        return true; // Row matches all filters
      });
    }

    return rows;
  });

  /** Computed: sorted data based on active sort state */
  readonly sortedData = computed(() => {
    const filtered = this.filteredData();
    const sortState = this.mainSortState();

    // If no sort is active, return filtered data unchanged
    if (!sortState) {
      return filtered;
    }

    // Sort the filtered data
    return this.sortRows(filtered, sortState.field, sortState.direction);
  });

  /** Computed: paginated data based on current page and page size */
  readonly paginatedData = computed(() => {
    const sorted = this.sortedData();

    // If pagination is not enabled, return all sorted data
    if (!this.enablePagination()) {
      return sorted;
    }

    const page = this.currentPage();
    const size = this.pageSize();
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;

    return sorted.slice(startIndex, endIndex);
  });

  /** Computed: total count for pagination (sorted data count) */
  readonly totalRecordCount = computed(() => this.sortedData().length);

  readonly isExpandableRowsAllowed = computed(() => this.config().expandable ?? false);

  // ============================================================================
  // CONSTRUCTOR & LIFECYCLE
  // ============================================================================

  constructor() {
    super();

    // Effect: Initialize expansion state when data or initiallyExpanded changes
    effect(() => {
      const rows = this.data();
      const shouldExpand = this.initiallyExpanded();

      // Use untracked to read current state without creating a dependency
      const currentState = untracked(() => this.expansionState());

      // Create new state map preserving existing expansion states
      const newState = new Map(currentState);

      rows.forEach((row) => {
        // Only set initial state for rows not already in the map
        if (!newState.has(row.id) && row.subData && row.subData.length > 0) {
          newState.set(row.id, shouldExpand);
        }
      });

      // Remove expansion states for rows no longer in data
      const currentIds = new Set(rows.map((r) => r.id));
      for (const id of newState.keys()) {
        if (!currentIds.has(id)) {
          newState.delete(id);
        }
      }

      this.expansionState.set(newState);
    });

    // Effect: Validate configuration when it changes
    effect(() => {
      const cfg = this.config();
      this.validateConfig(cfg);
    });

    // Effect: Validate data when it changes
    effect(() => {
      const rows = this.data();
      this.validateData(rows);
    });

    // Effect: Reset to page 1 when filters change
    effect(() => {
      this.filterValues(); // Create dependency on filter changes
      untracked(() => {
        // Only reset if not already on page 1
        if (this.currentPage() !== 1) {
          this.currentPage.set(1);
        }
      });
    });

    // Effect: Initialize column visibility when config changes
    effect(() => {
      const schema = this.config().mainSchema;

      // Only initialize if visibleColumnFields is empty
      if (untracked(() => this.visibleColumnFields().size === 0)) {
        const defaultVisible = new Set(schema.filter((col) => col.defaultVisible !== false).map((col) => col.field));
        this.visibleColumnFields.set(defaultVisible);
      }
    });
  }

  // ============================================================================
  // PUBLIC EXPANSION METHODS
  // ============================================================================

  /**
   * Toggle the expansion state of a specific row
   *
   * @param rowId The unique identifier of the row to toggle
   */
  toggleExpansion(rowId: string | number): void {
    // Don't allow expansion if table is not expandable
    if (!this.config().expandable) {
      return;
    }

    const currentState = this.expansionState();
    const isExpanded = currentState.get(rowId) || false;
    const newExpanded = !isExpanded;

    // Update expansion state
    const newState = new Map(currentState);
    newState.set(rowId, newExpanded);
    this.expansionState.set(newState);

    // Reset sub-table sort state when collapsing
    if (!newExpanded) {
      const subSorts = this.subTableSortStates();
      const newSubSorts = new Map(subSorts);
      newSubSorts.delete(rowId);
      this.subTableSortStates.set(newSubSorts);
    }

    // Find the row data for the event
    const row = this.data().find((r) => r.id === rowId);

    // Emit expansion change event
    if (row) {
      this.expansionChange.emit({
        rowId,
        expanded: newExpanded,
        row,
      });
    }
  }

  /**
   * Check if a specific row is currently expanded
   *
   * @param rowId The unique identifier of the row to check
   * @returns True if the row is expanded, false otherwise
   */
  isExpanded(rowId: string | number): boolean {
    return this.expansionState().get(rowId) || false;
  }

  /** Expand all rows that have sub-data */
  expandAll(): void {
    const newState = new Map(this.expansionState());
    this.data().forEach((row) => {
      if (row.subData && row.subData.length > 0) {
        newState.set(row.id, true);
      }
    });
    this.expansionState.set(newState);
  }

  /** Collapse all currently expanded rows */
  collapseAll(): void {
    const newState = new Map<string | number, boolean>();
    this.expansionState.set(newState);

    // Clear all sub-table sort states
    this.subTableSortStates.set(new Map());
  }

  /**
   * Sort the main table by a specific field
   *
   * @param field The field name to sort by
   * @param direction The sort direction ('asc', 'desc', or null to clear sort)
   */
  sortMainTable(field: string, direction: 'asc' | 'desc' | null): void {
    if (direction) {
      this.mainSortState.set({ field, direction });
      // Emit sort event
      this.mainTableSort.emit({ column: field, direction });
    } else {
      // Clear sort
      this.mainSortState.set(null);
      this.mainTableSort.emit({ column: field, direction: null });
    }
  }

  /**
   * Sort a sub-table by a specific field
   *
   * @param parentRowId The ID of the parent row containing the sub-table
   * @param field The field name to sort by
   * @param direction The sort direction ('asc', 'desc', or null to clear sort)
   */
  sortSubTable(parentRowId: string | number, field: string, direction: 'asc' | 'desc' | null): void {
    const newSubSorts = new Map(this.subTableSortStates());

    if (direction) {
      newSubSorts.set(parentRowId, { field, direction });
      // Emit sort event
      this.subTableSort.emit({ rowId: parentRowId, column: field, direction });
    } else {
      // Clear sort for this sub-table
      newSubSorts.delete(parentRowId);
      this.subTableSort.emit({ rowId: parentRowId, column: field, direction: null });
    }

    this.subTableSortStates.set(newSubSorts);
  }

  /**
   * Get the current sort state for a sub-table
   *
   * @param parentRowId The ID of the parent row
   * @returns The sort state object or null if not sorted
   */
  getSubTableSortState(parentRowId: string | number): { field: string; direction: 'asc' | 'desc' } | null {
    return this.subTableSortStates().get(parentRowId) || null;
  }

  /**
   * Get the sort direction for a main table column
   *
   * @param field The field name
   * @returns The sort direction or null if not sorted
   */
  getMainTableSortDirection(field: string): 'asc' | 'desc' | null {
    const sortState = this.mainSortState();
    return sortState?.field === field ? sortState.direction : null;
  }

  /**
   * Get the sort direction for a sub-table column
   *
   * @param parentRowId The ID of the parent row
   * @param field The field name
   * @returns The sort direction or null if not sorted
   */
  getSubTableSortDirection(parentRowId: string | number, field: string): 'asc' | 'desc' | null {
    const sortState = this.getSubTableSortState(parentRowId);
    return sortState?.field === field ? sortState.direction : null;
  }

  /**
   * Get sorted sub-table data for a row
   *
   * @param row The parent row
   * @returns The sub-table data, sorted if a sort state exists
   */
  getSubTableData(row: ManagedTableRow): Record<string, unknown>[] {
    if (!row.subData || row.subData.length === 0) {
      return [];
    }

    const sortState = this.getSubTableSortState(row.id);
    if (!sortState) {
      return row.subData;
    }

    return this.sortSubTableRows(row.subData, sortState.field, sortState.direction);
  }

  /**
   * Sort main table rows by a specific field
   *
   * @param rows The rows to sort
   * @param field The field name to sort by
   * @param direction The sort direction
   * @returns A new sorted array
   */
  private sortRows(rows: ManagedTableRow[], field: string, direction: 'asc' | 'desc'): ManagedTableRow[] {
    return [...rows].sort((a, b) => {
      const aValue = a.data[field];
      const bValue = b.data[field];
      return this.compareValues(aValue, bValue, direction);
    });
  }

  /**
   * Sort sub-table rows by a specific field
   *
   * @param rows The sub-table rows to sort
   * @param field The field name to sort by
   * @param direction The sort direction
   * @returns A new sorted array
   */
  private sortSubTableRows(
    rows: Record<string, unknown>[],
    field: string,
    direction: 'asc' | 'desc'
  ): Record<string, unknown>[] {
    return [...rows].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      return this.compareValues(aValue, bValue, direction);
    });
  }

  /**
   * Compare two values for sorting
   *
   * @param aValue First value
   * @param bValue Second value
   * @param direction Sort direction
   * @returns Comparison result (-1, 0, 1)
   */
  private compareValues(aValue: unknown, bValue: unknown, direction: 'asc' | 'desc'): number {
    // Handle null/undefined - push to end
    if (aValue === null || aValue === undefined) {
      if (bValue === null || bValue === undefined) {
        return 0;
      }
      return 1;
    }
    if (bValue === null || bValue === undefined) {
      return -1;
    }

    // Type-based comparison
    let result = 0;

    // String comparison (locale-aware, case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      result = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
    }
    // Number comparison
    else if (typeof aValue === 'number' && typeof bValue === 'number') {
      result = aValue - bValue;
    }
    // Boolean comparison
    else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      result = aValue === bValue ? 0 : aValue ? 1 : -1;
    }
    // Date comparison
    else if (aValue instanceof Date && bValue instanceof Date) {
      result = aValue.getTime() - bValue.getTime();
    }
    // Fallback: convert to string and compare
    else {
      const aStr = String(aValue);
      const bStr = String(bValue);
      result = aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
    }

    // Apply direction
    return direction === 'asc' ? result : -result;
  }

  // ============================================================================
  // FILTER METHODS
  // ============================================================================

  /**
   * Apply a filter to a specific column
   *
   * @param field The field name to filter
   * @param value The filter value (empty string clears the filter)
   */
  applyFilter(field: string, value: string): void {
    const newFilters = new Map(this.filterValues());

    if (value && value.trim()) {
      newFilters.set(field, value.trim());
    } else {
      newFilters.delete(field);
    }

    this.filterValues.set(newFilters);

    // Emit filter change event
    this.filterChange.emit({
      field,
      value: value.trim(),
      activeFilters: new Map(newFilters),
    });
  }

  /**
   * Clear the filter for a specific column
   *
   * @param field The field name to clear
   */
  clearFilter(field: string): void {
    this.applyFilter(field, '');
  }

  /** Clear all active filters */
  clearAllFilters(): void {
    this.filterValues.set(new Map());
  }

  /**
   * Get the current filter value for a specific field
   *
   * @param field The field name
   * @returns The current filter value or empty string if no filter
   */
  getFilterValue(field: string): string {
    return this.filterValues().get(field) || '';
  }

  /**
   * Check if a specific column has an active filter
   *
   * @param field The field name
   * @returns True if the column has an active filter
   */
  hasActiveFilter(field: string): boolean {
    const value = this.filterValues().get(field);
    return Boolean(value && value.trim());
  }

  /**
   * Check if any filters are currently active
   *
   * @returns True if any filters are active
   */
  hasAnyFilters(): boolean {
    return this.filterValues().size > 0;
  }

  // ============================================================================
  // GLOBAL SEARCH METHODS
  // ============================================================================

  /**
   * Apply global search value
   *
   * @param value The search value (empty string clears the search)
   */
  applyGlobalSearch(value: string): void {
    this.globalSearchValue.set(value);

    // Reset to page 1 when search changes
    if (this.enablePagination()) {
      this.currentPage.set(1);
    }
  }

  /** Clear the global search */
  clearGlobalSearch(): void {
    this.globalSearchValue.set('');
  }

  // ============================================================================
  // COLUMN CUSTOMIZATION METHODS
  // ============================================================================

  /** Open the column customization dialog Opens a full-height dialog positioned on the right side */
  openColumnCustomization(): void {
    const mainSchema = this.config().mainSchema;
    const visibleFields = this.visibleColumnFields();

    const dialogRef = this.dialogService.open<
      HxColumnCustomizationDialogComponent,
      unknown,
      unknown,
      ColumnCustomizationResult
    >(HxColumnCustomizationDialogComponent, {
      inputs: {
        columns: mainSchema,
        selectedFields: visibleFields,
      },
      position: 'right',
      customPosition: {
        top: '0',
        right: '0',
      },
      width: '300px',
      height: '100vh',
      maxHeight: '100vh',
      hasBackdrop: true,
      closeOnEscape: true,
    });

    dialogRef.afterClosed().subscribe((result: ColumnCustomizationResult | undefined) => {
      if (result?.confirmed) {
        this.visibleColumnFields.set(result.selections);
      }
    });
  }

  // ============================================================================
  // PAGINATION METHODS
  // ============================================================================

  /**
   * Navigate to a specific page
   *
   * @param page The page number (1-indexed)
   */
  goToPage(page: number): void {
    this.currentPage.set(page);
  }

  // ============================================================================
  // CELL RENDERER METHODS
  // ============================================================================

  /**
   * Build parameters object for cell renderer components
   *
   * Constructs the CellRendererParams object that gets passed to dynamically instantiated cell renderer components.
   *
   * @param row The current row data
   * @param column The column definition
   * @param rowIndex Zero-based row index in the current view
   * @param parentRowId Optional parent row ID (for sub-table cells)
   * @returns Params object for the cell renderer
   * @internal Used by the template to pass params to cell renderer components
   */
  buildParams(
    row: ManagedTableRow,
    column: ColumnDefinition,
    rowIndex: number,
    parentRowId?: string | number
  ): CellRendererParams {
    return {
      data: row.data,
      value: row.data[column.field],
      colDef: column,
      rowIndex,
      parentRowId,
    };
  }

  /**
   * Build inputs object for NgComponentOutlet
   *
   * Creates an object compatible with NgComponentOutlet's inputs property. Returns an object with a 'params' key
   * containing cell rendering context, plus any additional cellRendererParams from the column definition.
   *
   * @param row The current row data
   * @param column The column definition
   * @param rowIndex Zero-based row index in the current view
   * @param parentRowId Optional parent row ID (for sub-table cells)
   * @returns Object with params property and any cellRendererParams for NgComponentOutlet
   * @internal Used by the template for NgComponentOutlet binding
   */
  cellRendererInputs(
    row: ManagedTableRow,
    column: ColumnDefinition,
    rowIndex: number,
    parentRowId?: string | number
  ): { params: CellRendererParams } & Record<string, unknown> {
    return {
      params: this.buildParams(row, column, rowIndex, parentRowId),
      ...(column.cellRendererParams || {}),
    };
  }

  // ============================================================================
  // PRIVATE VALIDATION METHODS
  // ============================================================================

  /**
   * Validate the table configuration Note: Basic validation. For runtime schema validation, install and use Zod
   *
   * @private
   */
  private validateConfig(config: ManagedTableConfig): void {
    // Validate column definitions
    const validateColumns = (columns: ColumnDefinition[], schemaType: string): void => {
      columns.forEach((col, index) => {
        if (!col.field) {
          console.error(`[HxManagedTable] ${schemaType}[${index}]: field is required`);
        }
        if (!col.header) {
          console.error(`[HxManagedTable] ${schemaType}[${index}]: header is required`);
        }
      });
    };

    validateColumns(config.mainSchema, 'mainSchema');
    validateColumns(config.subSchema, 'subSchema');
  }

  /**
   * Validate the table data structure Note: Basic validation. For runtime schema validation, install and use Zod
   *
   * @private
   */
  private validateData(rows: ManagedTableRow[]): void {
    rows.forEach((row, index) => {
      if (row.id === undefined || row.id === null) {
        console.error(`[HxManagedTable] Data validation error: Row at index ${index} missing 'id' field`);
      }
      if (!row.data) {
        console.error(`[HxManagedTable] Data validation error: Row ${row.id} missing 'data' field`);
      }
    });
  }
}
